import type { Asset } from "@/lib/assets";
import { hazardKeyOf, hazardKeysFor, hazardTypeOfKey, type AssetHazardResult, type HazardKey } from "@/lib/results";
import { HAZARD_TYPES, type HazardType } from "@shared/scoring/types";

/** Business-importance / value-chain-stage-importance cutoff for "top" (High or Very High). */
const TOP_IMPORTANCE_MIN = 3;
/** Risk score at or above which an asset counts as "at high risk". */
export const HIGH_RISK_MIN = 3;

/** One summary line (see HazardKey): a hazard, or one precipitation season. */
export interface PerHazardHighRisk {
  key: HazardKey;
  hazardType: HazardType;
  /** Computed rows at risk >= HIGH_RISK_MIN. */
  high: number;
  /** Computed rows with a risk score — the denominator. Pending / no_data / not_applicable never count. */
  total: number;
  /** Rounded whole percent, or null (never 0) when there is nothing computed to divide by. */
  pct: number | null;
}

export interface Takeaways {
  mostAssetsAtRisk: HazardKey | null;
  riskiestForTopAssets: HazardKey | null;
  riskiestForTopValueChainStage: HazardKey | null;
  percentAtHighRisk: PerHazardHighRisk[];
}

/**
 * Extracted verbatim from TakeawayCards.tsx so the Tab 2 cards and the Tab 4
 * report compute the four spec'd takeaways (functional spec §3, Tab 2)
 * identically. Tie-breaking is deliberately unchanged and arbitrary: on equal
 * counts / equal max risk, the hazard first seen in `results` wins.
 *
 * `results` must already be sliced to one horizon/scenario (see
 * getResultsForHorizon) — it has one row per asset x hazard (per season for
 * changing_precipitation). Every count is per summary line (HazardKey), so
 * precipitation's MAM and OND are ranked and reported separately, never
 * combined.
 */
export function computeTakeaways(
  results: AssetHazardResult[],
  assets: Asset[],
  stageImportance: Record<string, number>,
  hazards: readonly HazardType[] = HAZARD_TYPES,
): Takeaways {
  const topAssetIds = new Set(assets.filter((a) => a.businessImportance >= TOP_IMPORTANCE_MIN).map((a) => a.id));
  const topStageAssetIds = new Set(
    assets
      .filter((a) => a.valueChainStageId && (stageImportance[a.valueChainStageId] ?? 0) >= TOP_IMPORTANCE_MIN)
      .map((a) => a.id),
  );

  return {
    mostAssetsAtRisk: hazardWithMostAssetsAtRisk(results),
    riskiestForTopAssets: riskiestHazardAmong(results, topAssetIds),
    riskiestForTopValueChainStage: riskiestHazardAmong(results, topStageAssetIds),
    percentAtHighRisk: percentAtHighRiskPerHazard(results, hazards),
  };
}

function hazardWithMostAssetsAtRisk(results: AssetHazardResult[]): HazardKey | null {
  const atRisk = results.filter((r) => r.status === "computed" && (r.risk ?? 0) >= HIGH_RISK_MIN);
  if (atRisk.length === 0) return null;

  const counts = new Map<HazardKey, number>();
  for (const r of atRisk) counts.set(hazardKeyOf(r), (counts.get(hazardKeyOf(r)) ?? 0) + 1);
  const [top] = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  return top[0];
}

/** The single highest-risk hazard among whichever asset ids are passed in,
 * or null if none of them have a computed risk. */
function riskiestHazardAmong(results: AssetHazardResult[], assetIds: Set<string>): HazardKey | null {
  const computedRows = results.filter((r) => r.status === "computed" && r.risk !== undefined && assetIds.has(r.assetId));
  if (computedRows.length === 0) return null;

  const [top] = [...computedRows].sort((a, b) => (b.risk ?? 0) - (a.risk ?? 0));
  return hazardKeyOf(top);
}

export function percentAtHighRiskPerHazard(
  results: AssetHazardResult[],
  hazards: readonly HazardType[] = HAZARD_TYPES,
  keys: readonly HazardKey[] = hazardKeysFor(hazards, results),
): PerHazardHighRisk[] {
  return keys.map((key) => {
    const computedRows = results.filter((r) => hazardKeyOf(r) === key && r.status === "computed" && r.risk !== undefined);
    const high = computedRows.filter((r) => (r.risk ?? 0) >= HIGH_RISK_MIN).length;
    const total = computedRows.length;
    return { key, hazardType: hazardTypeOfKey(key), high, total, pct: total === 0 ? null : Math.round((100 * high) / total) };
  });
}
